/**
 * appointmentPlanRequirement.ts
 *
 * The decisions behind the booking form's Plan field (SCRUM-2180), as pure
 * functions in their own module so each rule and transition can be unit-tested
 * without mounting the modal.
 *
 * `appointment_types.requiresPatientPlan` (see `@/lib/patientPlanRequirement`)
 * is 0 nothing, 1 a patient, 2 a patient and one of that patient's plans.
 * The server enforces it on save (`SchedulerWorkflowService::
 * enforceRequiresPatientPlanRule`) and answers a missing plan with a 422 on
 * `patientPlanId`. Everything here mirrors that rule so the form can say so
 * before the save instead of after it.
 */
import type { FieldValues, Resolver } from 'react-hook-form';
import type { PlanListItem } from '@/services/plans.service';
import type { PatientPlanRequirement } from '@/lib/patientPlanRequirement';

export const NO_PLANS_MESSAGE =
  'This appointment type needs a plan. Create a plan for this patient first, then book.';
export const SELECT_PLAN_MESSAGE = 'Select a plan';
export const LOAD_FAILED_MESSAGE = "Could not load this patient's plans. Try again.";

export function requiresPlan(requirement: PatientPlanRequirement): boolean {
  return requirement === 2;
}

/**
 * The form's plan id plus where it came from. Only a plan picked in the Plan
 * field (by the user, or by autoPickedPlan) is dropped when the type stops
 * needing one; a plan the modal was opened
 * with (booking from a plan's page, an attached plan, a just-created patient's
 * plan) links the appointment to that plan whatever the type, as before.
 */
export interface PlanSelection {
  planId: number | undefined;
  chosenInPicker: boolean;
  /** Set by autoPickedPlan: the patient's only plan, chosen without a click. */
  autoPicked?: boolean;
}

/**
 * A patient picked in the search. A different patient drops the plan (it
 * belonged to the previous one); picking the same patient again keeps it.
 */
export function planAfterPatientSelect(
  current: PlanSelection,
  previousPatientId: number | null | undefined,
  nextPatientId: number | null | undefined,
): PlanSelection {
  const same = Boolean(previousPatientId && previousPatientId > 0 && previousPatientId === nextPatientId);
  return same ? current : { planId: undefined, chosenInPicker: false };
}

export function planAfterTypeChange(
  current: PlanSelection,
  requirement: PatientPlanRequirement,
): PlanSelection {
  if (requiresPlan(requirement) || !(current.chosenInPicker || current.autoPicked)) return current;
  return { planId: undefined, chosenInPicker: false };
}

export interface AutoPickInput {
  isEditMode: boolean;
  requirement: PatientPlanRequirement;
  patientId: number | null | undefined;
  /** The form's plan right now. */
  planId: number | null | undefined;
  /** The patient's plans; `undefined` while they load or after a failed load. */
  plans: PlanListItem[] | undefined;
  /** The patient whose Plan field the user last emptied, if any. */
  clearedForPatientId: number | null;
}

/**
 * A new appointment of a "Patient & Plan" type for a patient with exactly one
 * plan gets that plan without a click. Returns null to leave the form's plan
 * alone: an appointment being edited, a plan already held (opened with,
 * picked, or picked here earlier), the list not loaded, any other number of
 * plans, or the user having emptied the field for this patient.
 */
export function autoPickedPlan(input: AutoPickInput): PlanSelection | null {
  if (input.isEditMode || !requiresPlan(input.requirement)) return null;
  if (!(input.patientId && input.patientId > 0)) return null;
  if (input.planId && input.planId > 0) return null;
  if (input.clearedForPatientId === input.patientId) return null;
  if (!input.plans || input.plans.length !== 1) return null;
  return { planId: input.plans[0].planId, chosenInPicker: false, autoPicked: true };
}

/**
 * The patient's plans were fetched again. A plan autoPickedPlan chose stands
 * only while it is still the patient's only plan: any other list empties the
 * field so the user picks. A plan the user picked, or one the modal was opened
 * with, is never touched. No list (loading, or a failed load) changes nothing.
 */
export function planAfterPlansRefresh(
  current: PlanSelection,
  plans: PlanListItem[] | undefined,
): PlanSelection {
  if (!current.autoPicked || current.chosenInPicker) return current;
  if (!(current.planId && current.planId > 0) || plans === undefined) return current;
  if (plans.length === 1 && plans[0].planId === current.planId) return current;
  return { planId: undefined, chosenInPicker: false };
}

export function planAfterPick(planId: number | null | undefined): PlanSelection {
  return planId && planId > 0
    ? { planId, chosenInPicker: true }
    : { planId: undefined, chosenInPicker: false };
}

/**
 * Mirrors the server's update exemption: a saved row that already lacked what
 * its type needed may be re-saved (rescheduled, etc.) without fixing it first.
 *
 * `savedRequirement` is null when the saved type is not in the form's type
 * list (an inactive type). The server still reads that type's setting, so a
 * row without a plan is treated as exempt rather than guessed strict.
 */
export function savedRowAlreadyMissesRequirement(args: {
  isEditMode: boolean;
  savedRequirement: PatientPlanRequirement | null;
  savedPatientId: number | null | undefined;
  savedPlanId: number | null | undefined;
}): boolean {
  if (!args.isEditMode) return false;
  if (args.savedRequirement === 2 || args.savedRequirement === null) {
    return !(args.savedPlanId && args.savedPlanId > 0);
  }
  if (args.savedRequirement === 1) return !(args.savedPatientId && args.savedPatientId > 0);
  return false;
}

export interface PlanFieldInput {
  requirement: PatientPlanRequirement;
  patientId: number | null | undefined;
  planId: number | null | undefined;
  /** The saved appointment's plan link is on screen and still the form's plan. */
  attachedPlanShown: boolean;
  savedRowAlreadyMisses: boolean;
  /** The patient's plans; `undefined` while they load or after a failed load. */
  plans: PlanListItem[] | undefined;
  /** The last load of the plans failed. */
  loadFailed: boolean;
}

export interface PlanFieldView {
  visible: boolean;
  /** Saving is refused without a plan. */
  required: boolean;
  /** Loaded, the patient has none, and the form holds none either. */
  noPlans: boolean;
  /** No list to choose from because loading it failed; offer a retry. */
  loadFailed: boolean;
  /** Shown under the field before any save attempt. */
  message: string | null;
  /** The validation error a save attempt raises; null = nothing to block. */
  error: string | null;
}

export function planFieldView(input: PlanFieldInput): PlanFieldView {
  const hasPatient = Boolean(input.patientId && input.patientId > 0);
  const visible = requiresPlan(input.requirement) && hasPatient && !input.attachedPlanShown;
  if (!visible) {
    return { visible: false, required: false, noPlans: false, loadFailed: false, message: null, error: null };
  }

  const hasPlan = Boolean(input.planId && input.planId > 0);
  // A failed reload keeps the earlier list, so only a missing list counts.
  const loadFailed = input.loadFailed && input.plans === undefined;
  const noPlans = !hasPlan && input.plans !== undefined && input.plans.length === 0;
  const required = !input.savedRowAlreadyMisses;
  const message = loadFailed ? LOAD_FAILED_MESSAGE : noPlans ? NO_PLANS_MESSAGE : null;
  const error = required && !hasPlan ? (message ?? SELECT_PLAN_MESSAGE) : null;

  return { visible, required, noPlans, loadFailed, message, error };
}

/**
 * The Plan field's options. A plan the form already holds (handed in by the
 * caller) that the patient's list lacks is kept as an option so the field
 * shows it rather than an empty "Select plan…".
 */
export function planOptions(
  plans: PlanListItem[] | undefined,
  heldPlanId: number | null | undefined,
): Array<{ value: number; label: string }> {
  const options = (plans ?? []).map((p) => ({ value: p.planId, label: planOptionLabel(p) }));
  if (heldPlanId && heldPlanId > 0 && !options.some((o) => o.value === heldPlanId)) {
    options.unshift({ value: heldPlanId, label: `Plan #${heldPlanId}` });
  }
  return options;
}

/**
 * Same wording as the plan picker in EnrollmentLinkageFields, so a plan reads
 * the same wherever staff choose one: "Plan #1042 - Name - $1500.00".
 */
export function planOptionLabel(plan: PlanListItem): string {
  const displayId = plan.displayId ? `Plan #${plan.displayId}` : `Plan #${plan.planId}`;
  const pieces = [
    displayId,
    plan.name?.trim() || null,
    plan.totalQuote != null ? `$${plan.totalQuote.toFixed(2)}` : null,
  ].filter(Boolean);
  return pieces.join(' - ');
}

/**
 * Wrap the form's schema resolver so a missing plan is a validation error like
 * any other: it lands on `patientPlanId`, shows under the field and in the
 * modal's banner, and the save never reaches the server. `planError` gets the
 * values being validated so it judges the plan actually being submitted.
 */
export function withPlanRequirement<T extends FieldValues>(
  base: Resolver<T>,
  planError: (values: T) => string | null,
): Resolver<T> {
  return async (values, context, options) => {
    const result = await base(values, context, options);
    const message = planError(values);
    if (!message) return result;
    return {
      values: {},
      errors: {
        ...result.errors,
        patientPlanId: { type: 'required', message },
      },
    } as Awaited<ReturnType<Resolver<T>>>;
  };
}
